import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import QuestionCreate from '../components/QuestionCreate';
import { questionApi } from '../api/question.api';
import { questionInitializer } from '../types/Question.initializer';
import type { CreateQuestionRequest } from '../types/Question.api.type';
import toast from 'react-hot-toast';

export default function QuestionCreatePage() {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  /**
   * States
   */
  const [createQuestionParam, setCreateQuestionParam] = useState<CreateQuestionRequest>(
    questionInitializer.INITIAL_CREATE_QUESTION_PARAM,
  );
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [step, setStep] = useState<number>(1);
  const [isDisplayingLoader, setIsDisplayingLoader] = useState<boolean>(false);

  const isContentStepValid = useMemo(() => {
    const { description, options } = createQuestionParam;
    const trimmedDescription = description.trim();

    if (trimmedDescription.length < 5 || trimmedDescription.length > 500) return false;
    if (options.length < 2 || options.length > 5) return false;

    const hasInvalidOption = options.some((option) => option.trim().length === 0 || option.trim().length > 100);
    if (hasInvalidOption) return false;

    const trimmedOptions = options.map((opt) => opt.trim().toLowerCase());
    const uniqueOptions = new Set(trimmedOptions);
    if (uniqueOptions.size !== options.length) return false;

    return true;
  }, [createQuestionParam]);

  /**
   * APIs
   */
  const { mutateAsync: createQuestionMutation } = useMutation({
    mutationFn: () => questionApi.createQuestion(createQuestionParam),
  });

  /**
   * Handlers
   */
  const setCreateQuestionParamHandler = (
    key: keyof CreateQuestionRequest,
    value: string | string[] | number | boolean,
  ) => {
    setCreateQuestionParam((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleNext = () => {
    if (step < 4) setStep((prev) => prev + 1);
  };
  const handlePrev = () => {
    if (step > 1) setStep((prev) => prev - 1);
  };
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCreateQuestionParamHandler('categoryId', category);
  };
  const handleSubmit = async () => {
    setIsDisplayingLoader(true);
    const minDelay = new Promise((resolve) => setTimeout(resolve, 2000));
    try {
      const [response] = await Promise.all([createQuestionMutation(), minDelay]);
      toast.success('설문이 성공적으로 등록되었어요.');
      setCreateQuestionParam(questionInitializer.INITIAL_CREATE_QUESTION_PARAM);
      await queryClient.invalidateQueries({ queryKey: ['questionList'] });
      const questionId = (response as any).data?.questionId || (response as any).questionId;
      if (questionId) {
        navigate(`/question/info/${questionId}`);
      } else {
        navigate('/');
      }
    } catch (error: any) {
      console.error(error);
      const code = error?.response?.data?.code;
      const msg = error?.response?.data?.message;
      if (code === 'ACTIVE_SURVEY_LIMIT' || code === 'COOLDOWN') {
        toast.error(msg ?? '설문 등록에 실패했어요. 다시 시도해주세요.');
      } else {
        toast.error('설문 등록에 실패했어요. 다시 시도해주세요.');
      }
      navigate('/');
    } finally {
      setIsDisplayingLoader(false);
    }
  };

  return (
    <div>
      {isDisplayingLoader && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="text-lg font-medium">설문을 등록중이에요...</p>
          </div>
        </div>
      )}
      <QuestionCreate
        selectedCategory={selectedCategory}
        handleCategoryChange={handleCategoryChange}
        step={step}
        handleNext={handleNext}
        handlePrev={handlePrev}
        handleSubmit={handleSubmit}
        createQuestionParam={createQuestionParam}
        setCreateQuestionParamHandler={setCreateQuestionParamHandler}
        isContentStepValid={isContentStepValid}
      />
    </div>
  );
}
